import React, { useEffect, useMemo, useRef, useState } from "react";
import { appService } from "../../App/app.service";
import {
  getDefaultSalesType,
  getDefaultSelectedFiscalQuarters,
  getDefaultSelectedStage,
  getUniqueValues,
} from "../../util/customFunctions";
import { fetchFirstLevelTeamNames, fetchDefaultTeamFilter } from "../rccUtils";
import { stackedBarPairedPalette } from "../colors";
import { useSelector, useDispatch } from "react-redux";
import D3MultiLineChart from "./D3MultiLineChart";
import RccCard from "../RccCard";

// Transform API data to multiline chart format
function transformCycleTimeData(apiData, timeKey) {
  if (!apiData || !Array.isArray(apiData) || apiData.length < 5) {
    return { chartData: [], categories: [], title: "" };
  }

  const segmentData = apiData[0] || []; // Data by segment and quarter
  const totalsByQuarter = apiData[1] || []; // Totals by quarter
  const totalsBySegment = apiData[2] || []; // Totals by segment
  const stageInfo = apiData[4] || []; // Stage information for title

  // Get unique quarters from segment data
  const uniqueQuarters = segmentData
    .map((item) => item?.[timeKey])
    .filter(getUniqueValues)
    .filter(Boolean)
    .sort();

  // Get unique segments and sort by total avg_cycle_time
  let uniqueSegments = totalsBySegment
    .map((item) => item.Acct_Size_Segment)
    .filter(getUniqueValues)
    .filter(Boolean);

  // Sort segments by total avg_cycle_time (descending)
  const sortedSegments = uniqueSegments
    .map((segment) => {
      const segmentData = totalsBySegment.find(
        (item) => item.Acct_Size_Segment === segment
      );
      return {
        segment,
        value: segmentData?.avg_cycle_time || 0,
      };
    })
    .sort((a, b) => b.value - a.value)
    .map((item) => item.segment);

  // Add "Total" if we have totals by quarter
  if (totalsByQuarter.length > 0) {
    uniqueSegments = [...sortedSegments, "Total"];
  } else {
    uniqueSegments = sortedSegments;
  }

  // Transform data to chart format
  const chartData = uniqueQuarters.map((quarter) => {
    const dataPoint = { quarter };
    uniqueSegments.forEach((segment) => {
      if (segment === "Total") {
        // Get total for this quarter
        const totalData = totalsByQuarter.find(
          (item) => item?.[timeKey] === quarter
        );
        dataPoint[segment] = totalData?.avg_cycle_time ?? null;
      } else {
        // Get segment value for this quarter
        const segmentDataPoint = segmentData.find(
          (item) =>
            item.Acct_Size_Segment === segment &&
            item?.[timeKey] === quarter
        );
        dataPoint[segment] = segmentDataPoint?.avg_cycle_time ?? null;
      }
    });
    return dataPoint;
  });

  // Generate title from stage info
  let title =
    "Average cycle time (Suspect to Won days) for Won deals by Customer Segment";
  if (stageInfo.length >= 2) {
    const fromStage = stageInfo[0]?.stage_name || "Identifying an Opportunity";
    const toStage =
      stageInfo[stageInfo.length - 1]?.stage_name?.split("','")[0] ||
      "Closed Won";
    title = `Average cycle time (${fromStage} to ${toStage} days) for Won deals by Customer Segment`;
  }

  return {
    chartData,
    categories: uniqueSegments,
    title,
  };
}

/**
 * Displays average cycle time by customer segment.
 * Inputs: Redux state (averageDealSizeData, company).
 * Outputs: Multi-line chart of cycle times over quarters.
 */
function AverageCycleTimeCard({ title, redirectToDashboard }) {
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [timeKey, setTimeKey] = useState(null);
  const [firstLevelTeamNames, setFirstLevelTeamNames] = useState([]);
  const [defaultTeamFilter, setDefaultTeamFilter] = useState([]);
  const [isFirstLevelTeamsLoaded, setIsFirstLevelTeamsLoaded] = useState(false);
  const [isDefaultTeamLoaded, setIsDefaultTeamLoaded] = useState(false);

  useEffect(() => {
    fetchFirstLevelTeamNames().then((names) => {
      setFirstLevelTeamNames(names);
      setIsFirstLevelTeamsLoaded(true);
    });
    fetchDefaultTeamFilter()
      .then((teams) => {
        setDefaultTeamFilter(teams);
        setIsDefaultTeamLoaded(true);
      })
      .catch((e) => {
        console.error("Error fetching default team filter:", e);
        setIsDefaultTeamLoaded(true);
      });
  }, []);

  const getTimeKeyFromFiltersResponse = (filtersResp) => {
    const message = filtersResp?.message;
    if (!Array.isArray(message)) return null;
    const meta = message.find(
      (item) =>
        item &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        typeof item.defaultTimeFrame === "string"
    );
    return meta?.defaultTimeFrame || null;
  };

  const getTimeKeyFromDataResponse = (dataResp) => {
    const dataArray = dataResp?.data || dataResp;
    if (!Array.isArray(dataArray)) return null;
    const marker = dataArray.find(
      (item) =>
        item &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        typeof item.type === "string"
    );
    return marker?.type || null;
  };

  const isDataLoadedForKey = (dataResp, key) => {
    if (!key) return false;
    const dataArray = dataResp?.data || dataResp;
    if (!Array.isArray(dataArray) || dataArray.length <= 1) return false;
    return getTimeKeyFromDataResponse(dataArray) === key;
  };

  // Get State
  const company = useSelector((state) => state.app.company);
  const cycleTimeData = useSelector(
    (state) => state.app.averageDealSizeData || null
  );

  // Check if data is loaded to determine Loading state
  const loadedKey = useMemo(
    () => getTimeKeyFromDataResponse(cycleTimeData?.data),
    [cycleTimeData]
  );
  const effectiveTimeKey = timeKey || loadedKey || "closed_fiscal_quarter";
  const isDataLoaded = isDataLoadedForKey(cycleTimeData, effectiveTimeKey);
  const isLoading = !isDataLoaded || !isFirstLevelTeamsLoaded || !isDefaultTeamLoaded;

  // Transform data for chart
  const chartConfig = useMemo(() => {
    if (!isDataLoaded) {
      return { chartData: [], categories: [], title: "" };
    }
    // Handle both data.data and direct data array
    const dataArray = cycleTimeData.data || cycleTimeData;
    if (!Array.isArray(dataArray) || dataArray.length < 5) {
      return { chartData: [], categories: [], title: "" };
    }
    return transformCycleTimeData(dataArray, effectiveTimeKey);
  }, [cycleTimeData, effectiveTimeKey, isDataLoaded]);

  useEffect(() => {
    if (fetchingRef.current) return;

    // If we already have data for the correct timeframe and filters applied, do nothing.
    if (isDataLoaded && appliedFilters.length > 0 && timeKey) return;

    if (!isFirstLevelTeamsLoaded || !isDefaultTeamLoaded) return;

    fetchingRef.current = true;
    dispatch({ type: "get_average_deal_size_filters_request" });

    appService
      .getAverageDealSizeFilters()
      .then((resp) => {
        dispatch({
          type: "get_average_deal_size_filters_success",
          json: resp,
        });

        const inferredTimeKey =
          getTimeKeyFromFiltersResponse(resp) || "closed_fiscal_quarter";
        setTimeKey(inferredTimeKey);

        // ---- Default stage logic ----
        const stageSequence = getDefaultSelectedStage(
          resp.message.filter((f) => f.type === "stage")
        );

        const defaultStages = resp.message
          .filter((item) => item.sequence >= stageSequence)
          .map((i) => i.stage_name)
          .filter(Boolean);

        // ---- Base filters ----
        let filters = [
          {
            name: inferredTimeKey,
            value: getDefaultSelectedFiscalQuarters(
              resp.message,
              inferredTimeKey
            ),
            string: true,
          },
          {
            name: "cycle_time_from_stage",
            value: defaultStages,
          },
        ];

        // ---- Company-specific logic ----
        if (company === "C0008") {
          const salesFilters = getDefaultSalesType(
            resp.message.filter((s) => s.type === "cross_sell1__c"),
            "cross_sell1__c"
          );

          const dealsFilters = getDefaultSalesType(
            resp.message.filter((s) => s.type === "Type"),
            "Type"
          );

          if (salesFilters[0] !== "All") {
            filters.push({
              name: "cross_sell1__c",
              value: salesFilters,
            });
          }

          if (dealsFilters[0] !== "All") {
            filters.push({
              name: "Type",
              value: dealsFilters,
            });
          }
        }

        // ---- Other defaults ----
        const defaultOppType = getDefaultSalesType(
          resp.message.filter((i) => i.type === "Type"),
          "Type"
        );

        const defaultSolutionCat = getDefaultSalesType(
          resp.message.filter((i) => i.type === "Solution_Cat"),
          "Solution_Cat"
        );

        if (defaultOppType[0] !== "All") {
          filters.push({ name: "Type", value: defaultOppType });
        }

        if (defaultSolutionCat[0] !== "All") {
          filters.push({
            name: "Solution_Cat",
            value: defaultSolutionCat,
          });
        }

        // Prioritize first-level teams, fallback to default team filter if not available
        const teamsToFilter =
          Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0
            ? firstLevelTeamNames
            : (Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0
                ? defaultTeamFilter
                : []);

        if (Array.isArray(teamsToFilter) && teamsToFilter.length > 0) {
          filters.push({ name: "team", value: teamsToFilter });
        }

        setAppliedFilters(filters);
        dispatch({ type: "current_selected_filters", filters });

        // ---- Fetch analysis data (if needed) ----
        const shouldFetchData = !isDataLoadedForKey(
          cycleTimeData,
          inferredTimeKey
        );
        if (!shouldFetchData) return null;

        dispatch({ type: "get_average_deal_size_data_request" });
        return appService.getAverageDealSizeData(
          resp.message.length === 0 ? [] : filters,
          inferredTimeKey
        );
      })
      .then((dataResp) => {
        fetchingRef.current = false;
        if (!dataResp) return;
        dispatch({
          type: "get_average_deal_size_data_success",
          json: dataResp,
        });
      })
      .catch((error) => {
        fetchingRef.current = false;
        dispatch({
          type: "get_average_deal_size_data_failure",
          error: typeof error === "object" ? "Something went wrong" : error,
        });
      });
  }, [
    dispatch,
    isDataLoaded,
    appliedFilters.length,
    timeKey,
    cycleTimeData,
    isFirstLevelTeamsLoaded,
    isDefaultTeamLoaded,
    firstLevelTeamNames,
    defaultTeamFilter,
  ]);

  const handleZoom = () => {
    // Logic to open zoom modal
    console.log("Zooming Cycle Time Chart...");
  };

  return (
    <RccCard
      title={
        title ||
        chartConfig.title ||
        "Average cycle time (Suspect to Won days) for Won deals by Customer Segment"
      }
      isLoading={isLoading}
      redirectToDashboard={redirectToDashboard || "Momentum Analysis"}
      onZoomClick={handleZoom}
      appliedFilters={appliedFilters}
      focusVariant={"current"}
    >
      {chartConfig.chartData.length > 0 && (
        <D3MultiLineChart
          data={chartConfig.chartData}
          categories={chartConfig.categories}
          colorPalette={stackedBarPairedPalette}
          options={{
            yAxisFormatter: (v) => Math.round(v),
            showLegend: true,
            marginTop: 20,
            marginBottom: 60,
          }}
        />
      )}
    </RccCard>
  );
}

export default AverageCycleTimeCard;
